/**
 * Seeded chart-of-accounts names by account CODE, for the document generators (receipts, investigation PDFs)
 * that print in the BRANCH's language rather than the reader's.
 *
 * A thin view over the one seeded-name dictionary (`../i18n/seeded-names.ts`, ADR-0030). There is no second
 * catalogue to keep in step: the API localizes every response from that dictionary, and this helper reads the
 * same entries by code.
 */
import { AppLanguage, parseAppLanguage } from "../i18n/app-language";
import { chartOfAccountsNameKey, SEEDED_NAME_DICTIONARY } from "../i18n/seeded-names";

const SEEDED_NAME_KEY = /^finance\.chartOfAccounts\.([^.]+)\.name$/;

const buildByCode = (language: AppLanguage): Readonly<Record<string, string>> =>
  Object.freeze(
    Object.fromEntries(
      Object.entries(SEEDED_NAME_DICTIONARY).flatMap(([key, entry]) => {
        const code = SEEDED_NAME_KEY.exec(key)?.[1];
        return code ? [[code, entry[language]]] : [];
      })
    )
  );

export const SEEDED_ACCOUNT_NAMES: Readonly<Record<string, Readonly<Record<string, string>>>> = Object.freeze({
  [AppLanguage.English]: buildByCode(AppLanguage.English),
  [AppLanguage.French]: buildByCode(AppLanguage.French),
  [AppLanguage.Kinyarwanda]: buildByCode(AppLanguage.Kinyarwanda)
});

/**
 * A seeded account's name in `language`, or null when the code is not seeded (a CUSTOM account) or the
 * language has no catalogue. Null rather than an English fallback: the caller holds the stored name, which is
 * the honest thing to fall back TO.
 */
export const seededAccountName = (code: string | null | undefined, language: string): string | null => {
  if (!code) return null;
  const resolved = parseAppLanguage(language);
  if (!resolved) return null;
  return SEEDED_NAME_DICTIONARY[chartOfAccountsNameKey(code)]?.[resolved] ?? null;
};
